import { Directive, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Directive({
  selector: '[dsRxForm]',
  exportAs: 'control',
})
export class DsRxFormsLinkDirective implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() dsRxForm: string = 'change';

  @HostBinding('disabled') disabled: boolean = false;
  @HostBinding('hasError') hasError: boolean = false;
  @HostBinding('errorMessage') errorMessage?: string;

  onChange!: (val: any) => void;
  onTouched!: () => void;
  listeners: Array<() => void> = [];

  constructor(
    private readonly renderer: Renderer2,
    private readonly host: ElementRef<HTMLElement>,
    public readonly control: NgControl
  ) {
    this.control.valueAccessor = this;
  }

  ngOnInit(): void {
    this.dispose = this.renderer.listen(this.host.nativeElement, this.dsRxForm, (e: CustomEvent<any>) =>
      this.onChange(e.detail)
    );

    /**
     * Observe for class list changes like, ng-*, etc...
     */
    const observer = new MutationObserver((mutationList) => {
      mutationList.filter((mutation) => mutation.attributeName === 'class').forEach(() => this.checkErrors());
    });
    observer.observe(this.host.nativeElement, { attributes: true });
    this.dispose = observer.disconnect.bind(observer);
  }

  ngOnDestroy(): void {
    this.dispose();
  }

  get dispose() {
    return () => {
      this.listeners.forEach((fn) => fn());
      this.listeners = [];
    };
  }

  set dispose(fn: () => void) {
    this.listeners.push(fn);
  }

  @HostListener('dSBlur')
  onBlur() {
    // Wait enough time so value change will be triggered first
    setTimeout(() => this.onTouched(), 100);
  }

  @HostListener('dSInput', ['$event.detail'])
  onWrappedInput(e: InputEvent) {
    const input = e.target as HTMLInputElement

    if (input.type === 'checkbox') {
      this.onChange(input.checked);
      return;
    }
    this.onChange(input.value);
  }

  hasValue() {
    return this.control.value !== undefined && this.control.value !== null;
  }

  equals(value: any) {
    return this.control.value === value;
  }

  checkErrors() {
    const { errors } = this.control;
    if (this.control.valid || !errors) {
      this.hasError = false;
      this.errorMessage = undefined;
      return;
    }

    if (errors.required && this.control.touched) {
      this.hasError = true;
      this.errorMessage = 'Campo requerido';
    }

    if (errors.legalAge) {
      this.hasError = true;
      this.errorMessage = 'Debe ser mayor de edad';
    }

    if (errors.minlength) {
      this.hasError = true;
      this.errorMessage = `Mínimo ${errors.minlength.requiredLength} caracteres`;
    }
  }

  writeValue(obj: any) {
    (this.host.nativeElement as any).value = obj;
  }

  registerOnChange(fn: any): void {

    this.onChange = (value) => {
      fn(value);
      this.checkErrors();
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }
}
